import { useShowAds } from "@repo/shared/redux";
import { useEffect } from "react";

// A sticky ad sits well under this; anything taller is a modal or consent wall.
const MAX_GUTTER = 300;
const MIN_GUTTER = 20;
// Creatives rotate up to this tall, so reserve it whenever any ad is up.
const MIN_RESERVE = 100;

// The gutter only pads below the fold, so measuring a beat late is invisible —
// and a hit test per scrolled frame forces a layout on every page.
const SETTLE_MS = 150;

const isPinned = (el: Element | null): boolean =>
  !!el &&
  el !== document.body &&
  (getComputedStyle(el).position === "fixed" || isPinned(el.parentElement));

// What is actually covering the bottom of the viewport right now, ignoring our
// own chrome (nav bar, floating buttons) which lives inside #root. The vendor
// pins a tall pointer-events:none box and hit tests only reach the ad inside
// it, so take the outermost hit that sits in a fixed ancestor.
const findAnchor = (): Element | null => {
  const root = document.getElementById("root");
  return (
    document
      .elementsFromPoint(
        Math.round(window.innerWidth / 2),
        window.innerHeight - 2,
      )
      .filter((el) => !root?.contains(el) && isPinned(el))
      .at(-1) ?? null
  );
};

const gutterOf = (anchor: Element | null): number => {
  if (!anchor) return 0;
  const covered = window.innerHeight - anchor.getBoundingClientRect().top;
  return covered >= MIN_GUTTER && covered <= MAX_GUTTER
    ? Math.max(Math.round(covered), MIN_RESERVE)
    : 0;
};

/*
 * Publishes --ad-gutter: the height of the sticky ad covering the bottom of the
 * viewport, or 0 when there is none. Read it through the `ad-gutter` /
 * `ad-gutter-pad` utilities rather than by hand.
 *
 * The spacers used to key off useShowAds(), which only means "not subscribed",
 * so every free user got a hardcoded hole whether or not an ad arrived. It
 * never arrives for adblock users, for EU visitors who leave the consent prompt
 * unanswered (the vendor holds every slot until then), or on a no-fill.
 *
 * Growing the gutter shifts nothing on screen: it only pads below the fold.
 */
export const useAdGutter = () => {
  const showAds = useShowAds();

  useEffect(() => {
    let published = -1;
    const set = (px: number) => {
      if (px === published) return;
      published = px;
      document.documentElement.style.setProperty("--ad-gutter", `${px}px`);
    };

    if (!showAds) {
      set(0);
      return;
    }

    // The anchor changing height in place — an ad refresh swapping creatives —
    // is announced by nothing else, so watch the box once we have found it.
    let observed: Element | null = null;
    const anchorResize = new ResizeObserver(([entry]) =>
      set(gutterOf(entry.target)),
    );

    const measure = () => {
      const anchor = findAnchor();
      if (anchor !== observed) {
        anchorResize.disconnect();
        if (anchor) anchorResize.observe(anchor);
        observed = anchor;
      }
      set(gutterOf(anchor));
    };

    let timer = 0;
    const probe = () => {
      clearTimeout(timer);
      timer = window.setTimeout(measure, SETTLE_MS);
    };

    measure();
    // Vendors append their anchor at body level to escape stacking contexts, so
    // a shallow childList watch is enough to know when to re-measure.
    const observer = new MutationObserver(probe);
    observer.observe(document.body, { childList: true });
    window.addEventListener("resize", probe);
    // Anchors that are pre-mounted and merely revealed on scroll mutate nothing.
    window.addEventListener("scroll", probe, { passive: true });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      anchorResize.disconnect();
      window.removeEventListener("resize", probe);
      window.removeEventListener("scroll", probe);
      set(0);
    };
  }, [showAds]);
};
